import React from 'react';
import { motion } from 'framer-motion';
import { NewsStoryProps } from './NewsStory';
import { MotionImage, useSanitizedTranslation } from 'ui';

export type NewsGridLayoutProps = {
  stories: NewsStoryProps[];
  accent: string;
  fullpage: boolean;
};

const imageVariants = {
  initial: {
    scale: 1,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
  hover: {
    scale: 1.04,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
};

export const StoryCard: React.FC<{ story: NewsStoryProps; postURL?: string; pressSidebar: boolean; fullpage: boolean }> = ({
  story,
  postURL,
  pressSidebar,
  fullpage,
}) => {
  const { t } = useSanitizedTranslation();
  const url = pressSidebar && postURL ? postURL : story.link ? story.link : (story.slug || '').replace(/^\/?/, '/');
  const linkOffsite = url.startsWith('/press') ? false : true;
  const storyDate = story.date
    ? new Date(story.date).toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <motion.a
      initial="initial"
      animate="initial"
      whileHover="hover"
      href={url}
      target={linkOffsite ? '_blank' : '_self'}
      className={`flex min-h-[365px] w-full cursor-pointer flex-col items-start overflow-hidden rounded-md bg-xumoWhite ${
        fullpage ? 'p-[20px]' : ''
      } tracking-tight shadow-none outline outline-1 outline-transparent hover:shadow-lg hover:outline-[#e7e5e4]`}
      style={{
        transition: 'box-shadow 0.2s, outline-color 0.2s',
        outlineColor: 'transparent',
      }}
    >
      <motion.div
        className={`relative flex aspect-video w-full items-center justify-center overflow-hidden ${fullpage ? 'rounded-md' : ''} bg-xumoBerry object-cover`}
      >
        <MotionImage
          src={story.thumbnail.src}
          alt={story.thumbnail.alt ?? ''}
          fill
          objectFit="cover"
          variants={imageVariants}
          sizes={'(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'}
        />
      </motion.div>
      <div className={`flex flex-1 flex-col ${fullpage ? '' : 'p-[20px]'} pt-6 ${pressSidebar ? 'gap-4' : ''}`}>
        <h3
          className={pressSidebar ? `type-h3-press text-xumoDarkPlum` : `press-newstory line-clamp-3 pb-2 text-left font-semibold text-black`}
          data-i18n={story.title}
          dangerouslySetInnerHTML={{ __html: t(story.title) }}
        />
        <div
          className={pressSidebar ? `type-small-body-press text-[#60545E]` : `text-body-base mb-2 line-clamp-3 w-full overflow-hidden text-left text-[#60545E]`}
          data-i18n={story.description}
          dangerouslySetInnerHTML={{ __html: t(story.description) }}
        />
        {!pressSidebar && <p className="text-body-base mt-auto pt-4 text-left text-xumoBlack opacity-60">{storyDate}</p>}
      </div>
    </motion.a>
  );
};

export const NewsGridLayout = ({ stories, accent, fullpage }: NewsGridLayoutProps) => {
  if (stories.length === 0) return null;

  const gridStories = stories.slice(0, 6);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {gridStories.map((story, index) => (
        <StoryCard key={story.title + index} story={story} fullpage={fullpage} />
      ))}
    </div>
  );
};
